import { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import type { ApiClient, Projekt } from '../api';
import { PageHeader } from '../rahmen/PageHeader';
import {
  ContentSection, DataTable, EmptyState, ErrorState, KpiCard, KpiReihe, LoadingState, StatusBadge,
  type Spalte,
} from '../muster';

interface Rahmen { api: ApiClient; projekt: Projekt | null }

/** Eine Zeile der Kabelliste (GET …/elektro/kabelliste), wie sie die API liefert. */
export interface Kabelzeile {
  art: 'kreis' | 'zuleitung';
  stromkreisId: string | null;
  verteilerKennzeichen: string;
  kennzeichen: string;
  bezeichnung: string | null;
  verwendung: string | null;
  istSicherheit: boolean;
  schutzorgan: string | null;
  kennlinie: string | null;
  inA: number | null;
  ibA: number | null;
  ibHerkunft: string | null;
  verlegeart: string | null;
  anordnung: string | null;
  haeufung: number | null;
  laengeM: number | null;
  kabeltyp: string | null;
  kabeltypHerkunft: string | null;
  funktionserhalt: string | null;
  parallel: number | null;
  querschnittMm2: number | null;
  peMm2: number | null;
  bezeichnungstext: string | null;
  querschnittBestand: number | null;
  massgebend: string | null;
  izA: number | null;
  duSummeProzent: number | null;
  duGrenzeProzent: number | null;
  zsOhm: number | null;
  ikMinEndeKa: number | null;
  ikMaxAnfangKa: number | null;
  iaA: number | null;
  schutzweg: string | null;
  i2tA2s: number | null;
  k2S2A2s: number | null;
  stufe: string;
  meldungen: string | null;
  ungeprueteNormwerte: number;
}

export interface Kabelstand {
  lauf: {
    id: string; beendetAm: string | null; datenstand: string | null; datenhash: string | null;
    kreise: number; zuleitungen: number; bestanden: number; warnungen: number; fehler: number; blocker: number;
  } | null;
  zeilen: Kabelzeile[];
}

const ZUSTAND: Record<string, 'gut' | 'warnung' | 'fehler' | 'info' | 'neutral'> = {
  BESTANDEN: 'gut', HINWEIS: 'info', WARNUNG: 'warnung', FEHLER: 'fehler', BLOCKER: 'fehler',
};

/**
 * Kabelliste und Kabeldimensionierung (EK, 16.10.2026).
 *
 * Die Seite LIEST den jüngsten Lauf von `kabel-bemessen`. Gerechnet wird
 * mit dem Werkzeug, nicht beim Aufruf. Excel gibt es hier, ein PDF-Knopf
 * fehlt bewusst: Der Bericht ELT-KAB entsteht über `berichte-erzeugen`, weil
 * jeder Abruf eine neue Berichtsnummer vergäbe (R25).
 */
export function ElektroKabelliste() {
  const { api, projekt } = useOutletContext<Rahmen>();
  if (!projekt) return <LoadingState />;
  return <KabellisteAnsicht api={api} projektId={projekt.id} />;
}

export function KabellisteAnsicht({ api, projektId }: { api: ApiClient; projektId: string }) {
  const [stand, setStand] = useState<Kabelstand | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [nurMaengel, setNurMaengel] = useState(false);

  useEffect(() => {
    let aktiv = true;
    api.elektroKabelliste(projektId)
      .then((s) => { if (aktiv) { setStand(s); setFehler(null); } })
      .catch((e: unknown) => { if (aktiv) setFehler(e instanceof Error ? e.message : String(e)); });
    return () => { aktiv = false; };
  }, [api, projektId]);

  const zeilen = useMemo(
    () => (stand?.zeilen ?? []).filter((z) => !nurMaengel || z.stufe === 'FEHLER' || z.stufe === 'BLOCKER'),
    [stand, nurMaengel]);

  const l = stand?.lauf;

  return (
    <div className="seite">
      <PageHeader
        titel="Kabelliste und Kabeldimensionierung"
        beschreibung="Je Zuleitung und Endstromkreis: Kabeltyp und Querschnitt, das maßgebende Kriterium und die Nachweise nach DIN VDE 0298-4, 0100-410, -430, -520 und -540. Nur lesend."
      />
      <p className="hinweis">
        Die Bemessung schreibt nicht in die Stromkreise. Belastbarkeitswerte über 4 mm² sind aus IEC 60364-5-52
        nachgebildet und nicht gegen DIN VDE 0298-4 geprüft; Kabeltypregeln, Schaltzustand für Ik-min und
        Spannungsfallgrenze sind unbestätigte Festlegungen.
      </p>
      {fehler && <ErrorState titel="Die Kabelliste konnte nicht geladen werden." technisch={fehler} />}
      {!fehler && !stand && <LoadingState />}
      {stand && !l && (
        <EmptyState titel="Noch keine Kabeldimensionierung" erklaerung="Gerechnet wird über das Werkzeug kabel-bemessen." />
      )}
      {l && (
        <>
          <KpiReihe>
            <KpiCard titel="Zuleitungen" wert={l.zuleitungen} />
            <KpiCard titel="Endstromkreise" wert={l.kreise} />
            <KpiCard titel="Bestanden" wert={l.bestanden} zustand="gut" />
            <KpiCard titel="Warnungen" wert={l.warnungen} zustand="warnung" />
            <KpiCard titel="Fehler" wert={l.fehler} zustand="fehler" />
            <KpiCard titel="Blocker" wert={l.blocker} zustand="fehler" />
          </KpiReihe>
          <p className="hinweis">Datenstand: {l.datenstand ?? '–'}</p>
          <div className="werkzeugleiste">
            <label>
              <input type="checkbox" checked={nurMaengel} onChange={(e) => setNurMaengel(e.target.checked)} />
              {' '}nur FEHLER und BLOCKER
            </label>
            <button type="button" onClick={() => { void api.elektroKabellisteExcel(projektId); }}>Excel</button>
          </div>
          <ContentSection titel="Verteilerzuleitungen">
            <Tabelle zeilen={zeilen.filter((z) => z.art === 'zuleitung')} />
          </ContentSection>
          <ContentSection titel="Endstromkreise">
            <Tabelle zeilen={zeilen.filter((z) => z.art === 'kreis')} />
          </ContentSection>
        </>
      )}
    </div>
  );
}

function Tabelle({ zeilen }: { zeilen: Kabelzeile[] }) {
  const spalten: Spalte<Kabelzeile>[] = [
    { schluessel: 'verteiler', titel: 'Verteiler', wert: (z) => z.verteilerKennzeichen },
    { schluessel: 'kennzeichen', titel: 'Kreis', wert: (z) => z.kennzeichen },
    { schluessel: 'verwendung', titel: 'Verwendung', wert: (z) => z.verwendung ?? (z.istSicherheit ? 'SV' : '') },
    { schluessel: 'schutz', titel: 'Schutz', wert: (z) => (z.kennlinie && z.inA ? `${z.kennlinie}${z.inA}` : z.inA ? `${z.inA} A` : '') },
    { schluessel: 'ib', titel: 'Ib', einheit: 'A', art: 'zahl', nachkomma: 1, wert: (z) => z.ibA },
    { schluessel: 'kabel', titel: 'Kabel', wert: (z) => `${z.bezeichnungstext ?? z.kabeltyp ?? ''}${z.funktionserhalt ? ` (${z.funktionserhalt})` : ''}` },
    { schluessel: 'bestand', titel: 'Bestand', einheit: 'mm²', art: 'zahl', nachkomma: 1, wert: (z) => z.querschnittBestand },
    { schluessel: 'laenge', titel: 'L', einheit: 'm', art: 'zahl', nachkomma: 1, wert: (z) => z.laengeM },
    { schluessel: 'iz', titel: 'Iz', einheit: 'A', art: 'zahl', nachkomma: 1, wert: (z) => z.izA },
    { schluessel: 'du', titel: 'Δu ab Ursprung', einheit: '%', art: 'zahl', nachkomma: 2, wert: (z) => z.duSummeProzent },
    { schluessel: 'zs', titel: 'Zs', einheit: 'Ω', art: 'zahl', nachkomma: 3, wert: (z) => z.zsOhm },
    { schluessel: 'ikmin', titel: 'Ik1min Ende', einheit: 'A', art: 'zahl', nachkomma: 0, wert: (z) => (z.ikMinEndeKa == null ? null : z.ikMinEndeKa * 1000) },
    { schluessel: 'ia', titel: 'Ia', einheit: 'A', wert: (z) => (z.iaA == null ? '' : `${z.iaA}${z.schutzweg === 'rcd-annahme' ? ' (RCD-Annahme)' : z.schutzweg === 'rcd' ? ' (RCD)' : ''}`) },
    { schluessel: 'i2t', titel: 'I²t / k²S²', art: 'zahl', nachkomma: 2, wert: (z) => (z.i2tA2s && z.k2S2A2s ? z.i2tA2s / z.k2S2A2s : null) },
    { schluessel: 'massgebend', titel: 'maßgebend', wert: (z) => z.massgebend },
    { schluessel: 'stufe', titel: 'Stufe', art: 'status', wert: (z) => z.stufe,
      inhalt: (z) => <StatusBadge zustand={ZUSTAND[z.stufe] ?? 'neutral'} text={z.stufe} titel={z.meldungen ?? undefined} klein /> },
  ];
  return (
    <DataTable name={`${zeilen.length} Zeilen`} spalten={spalten} zeilen={zeilen}
               schluessel={(z) => `${z.art}-${z.verteilerKennzeichen}-${z.kennzeichen}`} />
  );
}
